import { CalendarDays, RefreshCw, Target, TrendingDown, TrendingUp } from 'lucide-react';
import { useEffect, useState } from 'react';
import { apiRequest, ApiError } from '../../api/http';
import { WorkspaceLayout } from '../../layouts/WorkspaceLayout';
import { ErrorState, Spinner } from '../../shared/components';
import { useAuth } from '../../auth/AuthContext';
import { KpiTimelineChart } from './KpiTimelineChart';
import { bangkokDate, definitions, formatValue, metricStatus, shiftDate, targetLabel, timelineChange, type KpiData } from './kpiTimeline';
import './kpi.css';

const statusLabels = { 'on-track': 'ถึงเป้าแล้ว', watch: 'ใกล้เป้า', 'off-track': 'ยังไม่ถึงเป้า', 'no-data': 'ยังไม่มีข้อมูล' };

export function KpiPage() {
  const { session } = useAuth();
  const [today] = useState(() => bangkokDate());
  const [from, setFrom] = useState(() => shiftDate(today, -59));
  const [to, setTo] = useState(today);
  const [data, setData] = useState<KpiData | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const load = () => setRefresh(value => value + 1);
  const days = Math.round((Date.parse(to) - Date.parse(from)) / 86400000) + 1;

  useEffect(() => {
    const controller = new AbortController();
    setError('');
    if (!from || !to || !Number.isFinite(days) || days < 1 || days > 367) {
      setError('เลือกช่วงวันที่ 1–367 วัน โดยวันสิ้นสุดต้องไม่ก่อนวันเริ่มต้น');
      setLoading(false);
      return () => controller.abort();
    }
    setLoading(true);
    apiRequest<KpiData>('/admin/kpi', {
      query: { from, to, timezone: 'Asia/Bangkok', admin_id: session?.expert_id }, signal: controller.signal,
    }).then(result => { if (!controller.signal.aborted) setData(result); })
      .catch(reason => {
        if (!controller.signal.aborted) setError(reason instanceof ApiError ? reason.message : 'โหลด KPI ไม่สำเร็จ');
      }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [from, to, days, refresh, session?.expert_id]);

  const preset = (length: number) => { const end = bangkokDate(); setTo(end); setFrom(shiftDate(end, 1 - length)); };
  return <WorkspaceLayout kind="admin" title="KPI Timeline" description="แนวโน้มรายวันของ 6 KPI เทียบกับเป้า Day 60"
    actions={<button className="button button--ghost" onClick={load} disabled={loading}><RefreshCw size={17} />Refresh</button>}>
    <section className="panel date-panel kpi-dates">
      <div className="presets"><span><CalendarDays size={18} />ช่วงเวลา</span>{[7, 30, 60].map(length =>
        <button key={length} onClick={() => preset(length)} className={days === length ? 'active' : ''}>{length} วัน</button>)}</div>
      <label>จาก<input type="date" value={from} max={to} onChange={event => setFrom(event.target.value)} /></label>
      <label>ถึง<input type="date" value={to} min={from} onChange={event => setTo(event.target.value)} /></label>
      <span className="timezone">Asia/Bangkok · รายวัน</span>
    </section>
    {loading ? <div className="panel"><Spinner label="กำลังคำนวณแนวโน้ม KPI" /></div>
      : error ? <div className="panel"><ErrorState message={error} retry={load} /></div>
      : data && <>
        <KpiDashboard data={data} />
      </>}
  </WorkspaceLayout>;
}

export function KpiDashboard({ data }: { data: KpiData }) {
  const points = data.timeline?.points ?? [];
  return <>
        <div className="kpi-legend"><span><i className="kpi-legend__actual" />ค่าจริงรายวัน</span>
          <span><i className="kpi-legend__goal" />Benchmark · เป้า Day 60</span>
          <small>แตะกราฟเพื่อดูค่าของแต่ละวัน</small></div>
        <section className="kpi-timeline-grid">{definitions.map(definition => {
          const metric = data.metrics[definition.key];
          if (!metric) return null;
          const status = metricStatus(metric, definition);
          const delta = timelineChange(points, definition.key);
          const improvement = delta !== null && (definition.direction === 'down' ? delta < 0 : delta > 0);
          const deltaText = delta === null ? 'ข้อมูลต้นช่วงไม่พอเทียบ' : delta === 0 ? 'ไม่เปลี่ยนแปลงจากวันแรกของช่วง'
            : `${delta > 0 ? '+' : '−'}${definition.format === 'percent'
              ? `${(Math.abs(delta) * 100).toLocaleString('th-TH', { maximumFractionDigits: 1 })} จุดเปอร์เซ็นต์`
              : formatValue(Math.abs(delta), definition.format)} จากวันแรกของช่วง`;
          return <article className={`kpi-card kpi-card--${status} kpi-timeline-card`} key={definition.key}>
            <header><h2>{definition.label}</h2><b className="kpi-status">{statusLabels[status]}</b></header>
            <div className="kpi-timeline-value"><strong>{formatValue(metric.available === false ? null : metric.value, definition.format)}</strong>
              <span>ณ วันสิ้นสุดช่วง</span></div>
            <div className={`kpi-change ${delta === null || delta === 0 ? '' : improvement ? 'kpi-change--positive' : 'kpi-change--negative'}`}>
              {delta !== null && delta !== 0 && (delta > 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />)}{deltaText}</div>
            {points.length ? <KpiTimelineChart points={points} definition={definition} benchmark={metric.target} />
              : <div className="kpi-timeline-unavailable">ยังไม่มีข้อมูลแนวโน้มรายวัน กรุณา Refresh เมื่อ backend พร้อม</div>}
            <footer><span><Target size={16} />เป้า Day 60</span><b>{targetLabel(metric, definition)}</b></footer>
            <p className="kpi-timeline-note">{definition.note}</p>
          </article>;
        })}</section>
        <section className="panel kpi-freshness"><TrendingUp /><div><strong>คำนวณจากวันที่ของข้อมูลที่มีในระบบ</strong>
          <p>เส้นประเป็นเป้า Day 60 คงที่ · ช่วงที่ไม่มีตัวหารจะแสดงเป็นช่องว่าง</p>
          <p>สถานะรายการใช้สถานะปัจจุบัน · อัปเดต {new Date(data.generated_at).toLocaleString('th-TH', { timeZone: 'Asia/Bangkok' })}</p>
        </div></section>
  </>;
}
